import React, { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { ImageList, ImageListItem, Typography, Box, useTheme, useMediaQuery } from '@hermes/web-components'
import useTranslateMessage from '../../../hooks/useTranslateMessage'
import GalleryModal from './GalleryModal'
import getImageWithWWW from '../../../utils/getImageWithWWW'
import NextImage from 'next/image'

type GalleryProps = { images?: string[] }

const Gallery: FC<GalleryProps> = ({ images }) => {
  const theme = useTheme()
  const translate = useTranslateMessage()
  const onlySmallScreen = useMediaQuery(theme.breakpoints.down('sm'))

  const [showLightbox, setShowLightbox] = useState(false)
  const [selectedImage, setSelectedImage] = useState<number | undefined>()

  useEffect(() => {
    setShowLightbox(selectedImage !== undefined)
  }, [selectedImage])

  useEffect(() => {
    if (!showLightbox) {
      setSelectedImage(undefined)
    }
  }, [showLightbox])

  const galleryImages = useMemo(() => images?.map?.((image, index) => ({ id: index, image })) || [], [images])

  const toggleModal = useCallback(() => setShowLightbox((prev: boolean) => !prev), [])

  if (!images?.length) {
    return null
  }

  return (
    <Box>
      <Typography variant="h2" component="h2" sx={{ mb: 2 }}>
        {translate('gallery')}
      </Typography>
      <ImageList cols={onlySmallScreen ? 2 : 4} sx={{ overflow: 'hidden' }} rowHeight="auto">
        {galleryImages.map((image, idx) => (
          <ImageListItem key={`practice-${image.id}-${idx}`} sx={{ lineHeight: 0, aspectRatio: '1 / 1' }}>
            <NextImage
              loading="lazy"
              src={getImageWithWWW(image.image)}
              height={100}
              width={100}
              style={{ objectFit: 'cover', width: '100%', height: '100%', cursor: 'pointer' }}
              alt={image.image}
              onClick={() => setSelectedImage(image.id)}
            />
          </ImageListItem>
        ))}
      </ImageList>
      <GalleryModal
        isOpen={showLightbox}
        onClose={toggleModal}
        images={galleryImages}
        selectedImage={selectedImage}
        setSelectedImage={setSelectedImage}
      />
    </Box>
  )
}

export default Gallery
